import React, { useState, useEffect, useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";

function StudentAddressInfo({
  formData,
  handleChange,
  handleSubmit,
  isLoading,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    if (
      !(formData.address && formData.addressCountry && formData.addressCity)
    ) {
      setIsEditing(true);
    } else {
      setIsEditing(false);
    }
  }, [isLoading]);

  const handleSave = () => {
    // console.log("saving....")
    handleSubmit(); // Trigger the handleSubmit function passed in props
    setIsEditing(false); // Exit edit mode
  };

  const handleEdit = () => {
    setIsEditing(true); // Enter edit mode
  };

  useGSAP(
    () => {
      if (!isEditing || !formRef.current) {
        return;
      }

      const fields = formRef.current.querySelectorAll(".address-field");
      const cleanupFns = [];

      const focusBorderColor = "#22c55e";
      const filledBorderColor = "#6b7280";
      const emptyBorderColor = "#d1d5db";
      const errorBorderColor = "#ef4444";
      const clearBoxShadow = "0 0 0 0 rgba(0, 0, 0, 0)";

      fields.forEach((field) => {
        const input = field.querySelector("input, select, textarea");
        const label = field.querySelector("label");
        const helper = field.querySelector("p");

        if (!input || !label) {
          return;
        }

        const defaultHelperText = helper?.textContent ?? "";
        const normalizedHelperText = `Please enter your ${label.textContent.toLowerCase()}`;

        input.style.outline = "none";
        input.style.boxShadow = "none";

        const setInitialState = () => {
          if (input.value !== "") {
            gsap.set(label, {
              top: -10,
              fontSize: "0.70rem",
              color: "#6b7280",
            });
            gsap.set(input, {
              borderColor: filledBorderColor,
              boxShadow: clearBoxShadow,
            });
            helper?.classList.remove("text-red-500");
            helper?.classList.add("text-gray-500");
            if (helper) {
              helper.textContent = normalizedHelperText;
            }
          } else {
            gsap.set(label, {
              top: 10,
              fontSize: "1rem",
              color: "#6b7280",
            });
            gsap.set(input, {
              borderColor: emptyBorderColor,
              boxShadow: clearBoxShadow,
            });
            input.classList.remove("error-border");
            if (helper) {
              helper.textContent = defaultHelperText || normalizedHelperText;
              helper.classList.remove("text-red-500");
              helper.classList.add("text-gray-500");
            }
          }
        };

        setInitialState();

        const handleFocus = () => {
          gsap.to(label, {
            top: -10,
            fontSize: "0.70rem",
            color: "#22c55e",
            duration: 0.25,
          });
          gsap.to(input, {
            borderColor: focusBorderColor,
            duration: 0.25,
          });
          input.classList.remove("error-border");
          if (helper) {
            helper.textContent = normalizedHelperText;
            helper.classList.remove("text-red-500");
            helper.classList.add("text-gray-500");
          }
        };

        const handleInput = () => {
          if (input.value !== "") {
            gsap.to(label, { color: focusBorderColor, duration: 0.25 });
            gsap.to(input, {
              borderColor: focusBorderColor,
              duration: 0.25,
            });
            if (helper) {
              helper.textContent = normalizedHelperText;
              helper.classList.remove("text-red-500");
              helper.classList.add("text-gray-500");
            }
          }
        };

        const handleBlur = () => {
          if (input.value === "") {
            gsap.to(label, {
              top: 10,
              fontSize: "1rem",
              color: "#6b7280",
              duration: 0.25,
            });
            gsap.to(input, {
              borderColor: errorBorderColor,
              duration: 0.25,
            });
            input.classList.add("error-border");
            if (helper) {
              helper.textContent = `${label.textContent} Required *`;
              helper.classList.remove("text-gray-500");
              helper.classList.add("text-red-500");
            }
          } else {
            gsap.to(label, {
              top: -10,
              fontSize: "0.70rem",
              color: "#6b7280",
              duration: 0.25,
            });
            gsap.to(input, {
              borderColor: filledBorderColor,
              boxShadow: clearBoxShadow,
              duration: 0.25,
            });
            input.classList.remove("error-border");
            if (helper) {
              helper.textContent = normalizedHelperText;
              helper.classList.remove("text-red-500");
              helper.classList.add("text-gray-500");
            }
          }
        };

        input.addEventListener("focus", handleFocus);
        input.addEventListener("input", handleInput);
        input.addEventListener("blur", handleBlur);

        cleanupFns.push(() => {
          input.removeEventListener("focus", handleFocus);
          input.removeEventListener("input", handleInput);
          input.removeEventListener("blur", handleBlur);
        });
      });

      return () => {
        cleanupFns.forEach((cleanup) => cleanup());
      };
    },
    {
      scope: formRef,
      dependencies: [
        isEditing,
        formData.address,
        formData.addressCountry,
        formData.addressCity,
      ],
    }
  );

  return (
    <>
      <div className="flex flex-col space-y-6 w-[72%] text-left">
        {/* Existing Right Card */}
  <div className="px-10 py-6 bg-white rounded shadow-lg" ref={formRef}>
          {!isEditing && (
            <div className="flex justify-end absolute right-24">
              <button onClick={handleEdit}>
                <i className="fas fa-edit font-thin"></i>
              </button>
            </div>
          )}
          {/* Permanent Address Details */}
          <h2 className="text-gray-800 text-lg font-semibold mb-4">
            Address Details
          </h2>
          <hr className="border-t border-gray-300 mb-4" />

          {isEditing && (
            <>
              <div className="relative address-field">
                <label
                  htmlFor="address"
                  className="absolute text-sm text-gray-500 transform top-2 left-3 bg-white px-1 "
                >
                  Address Details
                </label>

                <textarea
                  id="address"
                  name="address"
                  value={formData.address} // Use formData to allow updates
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                ></textarea>
                <p className="text-gray-500 text-xs mt-1">
                  Please enter your Address Details
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 mt-6">
                {/* Country */}
                <div className="relative address-field">
                  <label
                    htmlFor="addressCountry"
                    className="absolute text-sm text-gray-500 transform top-2 left-3 bg-white px-1"
                  >
                    Country
                  </label>
                  <input
                    id="addressCountry"
                    name="addressCountry"
                    type="text"
                    value={formData.addressCountry} // Use formData to allow updates
                    onChange={handleChange}
                    className="w-full p-2 border rounded"
                  />
                  <p className="text-gray-500 text-xs mt-1">
                    Please Select Country
                  </p>
                </div>

                {/* City */}
                <div className="relative address-field">
                  <label
                    htmlFor="addressCity"
                    className="absolute text-sm text-gray-500 transform top-2 left-3 bg-white px-1"
                  >
                    City
                  </label>
                  <input
                    id="addressCity"
                    name="addressCity"
                    value={formData.addressCity} // Use formData to allow updates
                    onChange={handleChange}
                    className="w-full p-2 border rounded"
                  ></input>
                  <p className="text-gray-500 text-xs mt-1">
                    Please Select City
                  </p>
                </div>
              </div>
              <button
                onClick={handleSave}
                disabled={
                  !(
                    formData.address &&
                    formData.addressCountry &&
                    formData.addressCity
                  )
                }
                className={`px-4 py-2 rounded border 
                ${
                  formData.address &&
                  formData.addressCountry &&
                  formData.addressCity
                    ? "bg-white text-blue-500 border-blue-500 hover:bg-blue-500 hover:text-white cursor-pointer"
                    : "bg-white text-blue-200 border-blue-200 cursor-not-allowed"
                }`}
              >
                Save Address
              </button>
            </>
          )}

          {/* Display Saved Data */}
          {!isEditing && (
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Qualification Level */}
                <div className="p-4">
                  <span className="text-gray-800 text-base font-semibold">
                    Address:
                  </span>
                  <span className="text-gray-900 mt-1">
                    <br />
                    {formData.address}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6"></div>

              <div className="grid grid-cols-2 sm:grid-cols-2 gap-6">
                <div className="p-4">
                  <span className="text-gray-800 text-base font-semibold">
                    Country:
                  </span>
                  <span className="text-gray-900 mt-1">
                    <br />
                    {formData.addressCountry}
                  </span>
                </div>
                <div className="p-4">
                  <span className="text-gray-800 text-base font-semibold">
                    City:
                  </span>
                  <span className="text-gray-900 mt-1">
                    <br />
                    {formData.addressCity}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default StudentAddressInfo;
